import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

type ChartCardProps = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  minHeight?: number | string;
};

/**
 * Section card matching the Nektia "eyebrow + title + subtitle" header pattern.
 *
 * The accent (eyebrow + icon) uses primary.light on dark surfaces and
 * primary.main on light ones: raw primary.main is only ~3.5:1 on the dark
 * canvas, so the lighter electric-blue keeps small accent text AA-legible.
 */
export const ChartCard: React.FC<ChartCardProps> = ({
  eyebrow,
  title,
  subtitle,
  actions,
  icon,
  children,
  minHeight,
}) => {
  const theme = useTheme();
  const accent =
    theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main;

  return (
    <Card
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        height: "100%",
        minHeight,
        display: "flex",
        flexDirection: "column",
        transition: "border-color 200ms ease, box-shadow 200ms ease",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {icon && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
                color: accent,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box>
            {eyebrow && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: accent,
                  mb: 0.25,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Card>
  );
};
